import {
  PriceEvolutionData,
  PriceEvolutionByCategory,
  Insight,
} from './PriceEvolutionChart.types'

export const formatPrice = (
  price: number,
  isRental: boolean = false,
  isSqm: boolean = false
): string => {
  if (isNaN(price) || price === 0) return 'CHF 0'

  // Use 2 decimals for rental m² prices (typically CHF 15-35, where decimals matter)
  // Use 0 decimals for all other prices
  const useDecimals = isRental && isSqm
  const fractionDigits = useDecimals ? 2 : 0

  const formatter = new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  const formatted = useDecimals
    ? formatter.format(price)
    : formatter.format(Math.round(price))

  if (isRental) {
    if (isSqm) {
      return `${formatted}/m²/m`
    }
    return `${formatted}/m`
  }
  if (isSqm) {
    return `${formatted}/m²`
  }

  return formatted
}

export const formatShortPrice = (
  value: number,
  isRental: boolean = false,
  isSqm: boolean = false
): string => {
  // Use decimals for rental m² prices (small values where decimals matter)
  const useDecimals = isRental && isSqm

  if (value >= 1000000) {
    const millions = value / 1000000
    return `${millions.toFixed(millions >= 10 ? 0 : 1)}M`
  } else if (value >= 1000) {
    const thousands = value / 1000
    return `${thousands.toFixed(thousands >= 10 ? 0 : 1)}k`
  } else {
    return useDecimals ? value.toFixed(2) : value.toFixed(0)
  }
}

export const formatQuarter = (dateString: string): string => {
  const date = new Date(dateString)
  const month = date.getMonth()
  const year = date.getFullYear()
  const quarter = Math.floor(month / 3) + 1
  return `Q${quarter} ${year}`
}

export const formatPropertyCategory = (category: string): string => {
  return category
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Minimum sample size to include a quarter (filters out statistically unreliable data)
const MIN_SAMPLE_SIZE = 10

// Helper to round price values based on whether decimals should be preserved
const roundPrice = (value: number, preserveDecimals: boolean): number => {
  if (preserveDecimals) {
    // Round to 2 decimal places for rental m² prices
    return Math.round(value * 100) / 100
  }
  return Math.round(value)
}

// Apply weighted moving average smoothing
// Uses linear weights where recent quarters have higher influence
// Window size of 4 quarters (1 year) with weights [1, 2, 3, 4]
const applyWeightedMovingAverage = (
  data: PriceEvolutionData[],
  windowSize: number = 4,
  preserveDecimals: boolean = false
): PriceEvolutionData[] => {
  if (data.length < 2) return data

  return data.map((point, index) => {
    // Get window of quarters (current + previous)
    const start = Math.max(0, index - windowSize + 1)
    const window = data.slice(start, index + 1)

    // Linear weights: [1, 2, 3, 4] - recent = higher weight
    let totalWeight = 0
    let weightedMedian = 0
    let weightedAvg = 0
    let weightedCount = 0

    window.forEach((p, i) => {
      const weight = i + 1
      weightedMedian += p.medianPrice * weight
      weightedAvg += p.avgPrice * weight
      weightedCount += p.count * weight
      totalWeight += weight
    })

    const smoothed: PriceEvolutionData = {
      ...point,
      medianPrice: roundPrice(weightedMedian / totalWeight, preserveDecimals),
      avgPrice: roundPrice(weightedAvg / totalWeight, preserveDecimals),
      count: Math.round(weightedCount / totalWeight),
    }

    // Smooth quantiles if present in all window points
    if (point.quantiles && window.every((p) => p.quantiles)) {
      smoothed.quantiles = {
        q10: roundPrice(
          window.reduce((s, p, i) => s + (p.quantiles?.q10 || 0) * (i + 1), 0) /
            totalWeight,
          preserveDecimals
        ),
        q30: roundPrice(
          window.reduce((s, p, i) => s + (p.quantiles?.q30 || 0) * (i + 1), 0) /
            totalWeight,
          preserveDecimals
        ),
        q50: roundPrice(
          window.reduce((s, p, i) => s + (p.quantiles?.q50 || 0) * (i + 1), 0) /
            totalWeight,
          preserveDecimals
        ),
        q70: roundPrice(
          window.reduce((s, p, i) => s + (p.quantiles?.q70 || 0) * (i + 1), 0) /
            totalWeight,
          preserveDecimals
        ),
        q90: roundPrice(
          window.reduce((s, p, i) => s + (p.quantiles?.q90 || 0) * (i + 1), 0) /
            totalWeight,
          preserveDecimals
        ),
      }
    }

    return smoothed
  })
}

// Filter out quarters with too few listings (statistically unreliable)
const filterByMinSampleSize = (
  data: PriceEvolutionData[],
  minCount: number = MIN_SAMPLE_SIZE
): PriceEvolutionData[] => {
  return data.filter((point) => point.count >= minCount)
}

export const processTimeSeriesData = (
  aggregations: any,
  preserveDecimals: boolean = false
): PriceEvolutionByCategory => {
  if (!aggregations?.price_over_time?.buckets) {
    return {}
  }

  const rawBuckets = aggregations.price_over_time.buckets
  const propertyCategories = new Set<string>()
  const timeSeriesData: PriceEvolutionByCategory = {}
  const allTypesData: PriceEvolutionData[] = []

  // Check if buckets is an object (keyed by quarter like "2024-Q1") or array
  const isKeyedBuckets =
    rawBuckets && typeof rawBuckets === 'object' && !Array.isArray(rawBuckets)

  // Convert quarter key (e.g., "2024-Q1") to date string (e.g., "2024-01-01")
  const quarterKeyToDateString = (quarterKey: string): string => {
    const match = quarterKey.match(/^(\d{4})-Q(\d)$/)
    if (match) {
      const year = match[1]
      const quarter = parseInt(match[2])
      const month = String((quarter - 1) * 3 + 1).padStart(2, '0')
      return `${year}-${month}-01`
    }
    return quarterKey
  }

  // Normalize buckets to array format for processing
  let buckets: Array<{ key: string; data: any }>

  if (isKeyedBuckets) {
    // Convert keyed buckets to array and sort by quarter
    buckets = Object.entries(rawBuckets)
      .map(([key, data]) => ({ key, data }))
      .sort((a, b) => a.key.localeCompare(b.key))
  } else {
    // Already an array (legacy format)
    buckets = rawBuckets.map((bucket: any) => ({
      key: bucket.key_as_string,
      data: bucket,
    }))
  }

  // For legacy array format, calculate min/max dates for quarter completeness check
  let minDate: Date | null = null
  let maxDate: Date | null = null

  if (!isKeyedBuckets) {
    buckets.forEach(({ key }) => {
      const date = new Date(key)
      if (!minDate || date < minDate) minDate = date
      if (!maxDate || date > maxDate) maxDate = date
    })
  }

  const isQuarterComplete = (
    quarterDate: Date,
    minDate: Date,
    maxDate: Date
  ): boolean => {
    const quarterMonth = quarterDate.getMonth()
    const quarterYear = quarterDate.getFullYear()
    const quarterStart = new Date(quarterYear, quarterMonth, 1)
    const quarterEnd = new Date(quarterYear, quarterMonth + 3, 0)
    return quarterStart >= minDate && quarterEnd <= maxDate
  }

  // Collect all property categories
  buckets.forEach(({ data }) => {
    const bucketData = isKeyedBuckets ? data : data
    if (bucketData.by_property_category?.buckets) {
      bucketData.by_property_category.buckets.forEach((categoryBucket: any) => {
        propertyCategories.add(categoryBucket.key)
      })
    }
  })

  propertyCategories.forEach((category) => {
    timeSeriesData[category] = []
  })

  buckets.forEach(({ key, data }) => {
    const dateString = isKeyedBuckets ? quarterKeyToDateString(key) : key
    const bucketDate = new Date(dateString)
    const bucketData = isKeyedBuckets ? data : data

    // For legacy format, check quarter completeness
    if (!isKeyedBuckets) {
      if (
        !minDate ||
        !maxDate ||
        !isQuarterComplete(bucketDate, minDate, maxDate)
      ) {
        return
      }
    }

    let totalCount = 0

    propertyCategories.forEach((category) => {
      const categoryBucket = bucketData.by_property_category?.buckets?.find(
        (b: any) => b.key === category
      )

      if (categoryBucket?.median_price?.values) {
        const medianPrice = roundPrice(
          categoryBucket.median_price.values['50.0'] || 0,
          preserveDecimals
        )
        const count = categoryBucket.listing_count?.value || 0

        const dataPoint: PriceEvolutionData = {
          date: dateString,
          avgPrice: roundPrice(
            categoryBucket.avg_price?.value || 0,
            preserveDecimals
          ),
          medianPrice,
          count,
        }

        // Add quantiles if available
        if (categoryBucket.quantiles?.values) {
          dataPoint.quantiles = {
            q10: roundPrice(
              categoryBucket.quantiles.values['10.0'] || 0,
              preserveDecimals
            ),
            q30: roundPrice(
              categoryBucket.quantiles.values['30.0'] || 0,
              preserveDecimals
            ),
            q50: roundPrice(
              categoryBucket.quantiles.values['50.0'] || 0,
              preserveDecimals
            ),
            q70: roundPrice(
              categoryBucket.quantiles.values['70.0'] || 0,
              preserveDecimals
            ),
            q90: roundPrice(
              categoryBucket.quantiles.values['90.0'] || 0,
              preserveDecimals
            ),
          }
        }

        timeSeriesData[category].push(dataPoint)
        totalCount += count
      }
    })

    if (bucketData.overall_median?.values && totalCount > 0) {
      const overallMedian = roundPrice(
        bucketData.overall_median.values['50.0'] || 0,
        preserveDecimals
      )

      const allTypesDataPoint: PriceEvolutionData = {
        date: dateString,
        avgPrice: roundPrice(
          bucketData.total_avg_price?.value || 0,
          preserveDecimals
        ),
        medianPrice: overallMedian,
        count: totalCount,
      }

      // Add overall quantiles if available
      if (bucketData.overall_quantiles?.values) {
        allTypesDataPoint.quantiles = {
          q10: roundPrice(
            bucketData.overall_quantiles.values['10.0'] || 0,
            preserveDecimals
          ),
          q30: roundPrice(
            bucketData.overall_quantiles.values['30.0'] || 0,
            preserveDecimals
          ),
          q50: roundPrice(
            bucketData.overall_quantiles.values['50.0'] || 0,
            preserveDecimals
          ),
          q70: roundPrice(
            bucketData.overall_quantiles.values['70.0'] || 0,
            preserveDecimals
          ),
          q90: roundPrice(
            bucketData.overall_quantiles.values['90.0'] || 0,
            preserveDecimals
          ),
        }
      }

      allTypesData.push(allTypesDataPoint)
    }
  })

  const minDataPoints = 2
  const significantCategories = Object.entries(timeSeriesData)
    .filter(([_category, data]) => {
      return data.length >= minDataPoints
    })
    .reduce((acc, [category, data]) => {
      // Apply smoothing pipeline:
      // 1. Filter by minimum sample size (removes statistically unreliable quarters)
      // 2. Apply weighted moving average (smooths quarter-to-quarter variation)
      const filtered = filterByMinSampleSize(data)
      const smoothed = applyWeightedMovingAverage(filtered, 4, preserveDecimals)

      // Only include if we still have enough data points after filtering
      if (smoothed.length >= minDataPoints) {
        acc[category] = smoothed
      }
      return acc
    }, {} as PriceEvolutionByCategory)

  if (allTypesData.length >= minDataPoints) {
    // Apply same smoothing to "All Types" data
    const filteredAllTypes = filterByMinSampleSize(allTypesData)
    const smoothedAllTypes = applyWeightedMovingAverage(
      filteredAllTypes,
      4,
      preserveDecimals
    )

    if (smoothedAllTypes.length >= minDataPoints) {
      significantCategories['All Types'] = smoothedAllTypes
    }
  }

  return significantCategories
}

export const calculateKPIs = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median'
) => {
  const allPrices: number[] = []
  const latestPrices: Record<string, number> = {}
  const firstPrices: Record<string, number> = {}
  let totalVolume = 0

  Object.entries(data).forEach(([category, values]) => {
    if (values.length > 0 && category !== 'All Types') {
      const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
      latestPrices[category] = values[values.length - 1][priceField]
      firstPrices[category] = values[0][priceField]
      values.forEach((v) => {
        allPrices.push(v[priceField])
        totalVolume += v.count
      })
    }
  })

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const avgPrice =
    data['All Types'] && data['All Types'].length > 0
      ? data['All Types'][data['All Types'].length - 1][priceField]
      : allPrices.length > 0
        ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length
        : 0

  const mostExpensiveCategory = Object.entries(latestPrices)
    .filter(([category]) => category !== 'All Types')
    .sort((a, b) => b[1] - a[1])[0]

  let overallGrowth = 0
  if (data['All Types'] && data['All Types'].length > 0) {
    const allTypesData = data['All Types']
    const latest = allTypesData[allTypesData.length - 1]
    const oneYearAgoIndex = Math.max(0, allTypesData.length - 5)
    const oneYearAgoData = allTypesData[oneYearAgoIndex]

    if (oneYearAgoData && oneYearAgoData[priceField] > 0) {
      const oneYearAgoPrice = oneYearAgoData[priceField]
      const latestPrice = latest[priceField]
      overallGrowth = ((latestPrice - oneYearAgoPrice) / oneYearAgoPrice) * 100
    }
  } else {
    const growthRates: number[] = []
    Object.entries(data).forEach(([category, values]) => {
      if (category !== 'All Types' && values.length >= 5) {
        const latest = values[values.length - 1]
        const oneYearAgoIndex = Math.max(0, values.length - 5)
        const oneYearAgo = values[oneYearAgoIndex]

        if (oneYearAgo && oneYearAgo[priceField] > 0) {
          const growth =
            ((latest[priceField] - oneYearAgo[priceField]) /
              oneYearAgo[priceField]) *
            100
          growthRates.push(growth)
        }
      }
    })

    overallGrowth =
      growthRates.length > 0
        ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
        : 0
  }

  return {
    avgPrice,
    overallGrowth,
    mostExpensiveCategory,
    totalVolume,
  }
}

export const getChartColors = (): Record<string, string> => ({
  apartment: '#2563eb',
  house: '#10b981',
  commercial: '#f59e0b',
  plot: '#ef4444',
  'all types': '#8b5cf6',
})

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median',
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (Object.keys(data).length === 0) return insights

  const categories = Object.keys(data)
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

  const allTypesData = data['All Types']
  if (allTypesData && allTypesData.length >= 2) {
    const firstQuarter = allTypesData[0]
    const lastQuarter = allTypesData[allTypesData.length - 1]
    const overallChange =
      ((lastQuarter[priceField] - firstQuarter[priceField]) /
        firstQuarter[priceField]) *
      100

    insights.push({
      title: t('charts.insights.marketTrend'),
      description: t('charts.insights.descriptions.priceEvolution.marketTrend', {
        direction:
          overallChange >= 0
            ? t('charts.common.increased') || 'increased'
            : t('charts.common.decreased') || 'decreased',
        change: Math.abs(overallChange).toFixed(1),
        priceType: priceType === 'median' ? t('charts.priceTypes.median') || 'median' : t('charts.priceTypes.average') || 'average',
        startPrice: formatPrice(firstQuarter[priceField]),
        endPrice: formatPrice(lastQuarter[priceField]),
      }),
    })
  }

  const categoryGrowth = categories
    .filter((cat) => cat !== 'All Types' && data[cat].length >= 2)
    .map((category) => {
      const catData = data[category]
      const firstPrice = catData[0][priceField]
      const lastPrice = catData[catData.length - 1][priceField]
      const growth = ((lastPrice - firstPrice) / firstPrice) * 100
      return { category, growth }
    })
    .sort((a, b) => b.growth - a.growth)

  if (categoryGrowth.length > 0) {
    const best = categoryGrowth[0]
    insights.push({
      title: t('charts.insights.bestPerformer'),
      description: t('charts.insights.descriptions.priceEvolution.bestPerformer', {
        category: formatPropertyCategory(best.category),
        growth: best.growth.toFixed(1),
      }),
    })
  }

  const allVolatilities = categories
    .map((category) => {
      const catData = data[category]
      if (catData.length < 3) return null

      const changes = []
      for (let i = 1; i < catData.length; i++) {
        const change = Math.abs(
          ((catData[i][priceField] - catData[i - 1][priceField]) /
            catData[i - 1][priceField]) *
            100
        )
        changes.push(change)
      }

      const avgVolatility = changes.reduce((a, b) => a + b, 0) / changes.length
      return { category, volatility: avgVolatility }
    })
    .filter(Boolean) as Array<{ category: string; volatility: number }>

  if (allVolatilities.length > 0) {
    const mostStable = allVolatilities.reduce((min, curr) =>
      curr.volatility < min.volatility ? curr : min
    )

    insights.push({
      title: t('charts.insights.marketStability'),
      description: t('charts.insights.descriptions.priceEvolution.marketStability', {
        category: formatPropertyCategory(mostStable.category),
        volatility: mostStable.volatility.toFixed(1),
      }),
    })
  }

  if (allTypesData && allTypesData.length >= 5) {
    const recentData = allTypesData.slice(-4)
    const recentChange =
      ((recentData[3][priceField] - recentData[0][priceField]) /
        recentData[0][priceField]) *
      100
    const momentum =
      recentChange > 2
        ? t('charts.common.strongUpward') || 'strong upward'
        : recentChange < -2
          ? t('charts.common.downward') || 'downward'
          : t('charts.common.stable') || 'stable'

    insights.push({
      title: t('charts.insights.recentMomentum'),
      description: t('charts.insights.descriptions.priceEvolution.recentMomentum', {
        momentum,
        change: Math.abs(recentChange).toFixed(1),
        direction:
          recentChange >= 0
            ? t('charts.common.increase') || 'increase'
            : t('charts.common.decrease') || 'decrease',
        priceType,
      }),
    })
  }

  return insights
}
